import { AxiosError, AxiosResponse } from 'axios';
import api from './api';

const DOWNLOADABLE_CONTENT_TYPES = [
  'application/pdf',
  'application/zip',
  'application/x-zip-compressed',
  'application/octet-stream',
];
const DEFAULT_FULL_FILENAME = 'prescricao.pdf';
const DEFAULT_INDIVIDUAL_FILENAME = 'prescricao-individual.zip';
const OBJECT_URL_REVOKE_DELAY_MS = 1000;

export interface PrescriptionDownloadResult {
  blob: Blob;
  filename: string;
}

export interface DownloadPrescriptionPdfOptions {
  defaultFilename?: string;
}

const postPrescriptionDocument = (access: string): Promise<AxiosResponse<Blob>> =>
  api.post('/patients/prescriptions/pdf', { access }, { responseType: 'blob' });

function parseFilenameFromContentDisposition(disposition: string): string | null {
  if (!disposition) {
    return null;
  }

  const utf8Match = /filename\*=UTF-8''([^;\n]+)/i.exec(disposition);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const match = /filename="?([^";\n]+)"?/i.exec(disposition);
  return match?.[1]?.trim() ?? null;
}

function parseDownloadResponse(
  response: AxiosResponse<Blob>,
  defaultFilename: string,
): PrescriptionDownloadResult {
  const contentType = String(response.headers?.['content-type'] ?? '').toLowerCase();

  if (!DOWNLOADABLE_CONTENT_TYPES.some((type) => contentType.includes(type))) {
    throw new Error('Resposta inválida ao baixar documento');
  }

  const disposition = response.headers?.['content-disposition'] ?? '';
  const filename = parseFilenameFromContentDisposition(String(disposition)) ?? defaultFilename;

  return {
    blob: response.data,
    filename,
  };
}

export async function downloadPrescriptionPdf(
  pdfAccess: string,
  refreshPdfAccess: () => Promise<string | null>,
  options: DownloadPrescriptionPdfOptions = {},
): Promise<PrescriptionDownloadResult> {
  const defaultFilename = options.defaultFilename ?? DEFAULT_FULL_FILENAME;

  try {
    const response = await postPrescriptionDocument(pdfAccess);
    return parseDownloadResponse(response, defaultFilename);
  } catch (error) {
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 401) {
      const newAccess = await refreshPdfAccess();
      if (!newAccess) {
        throw new Error('Documento indisponível após refresh');
      }
      const retryResponse = await postPrescriptionDocument(newAccess);
      return parseDownloadResponse(retryResponse, defaultFilename);
    }
    throw error;
  }
}

export function triggerBlobDownload(blob: Blob, filename = DEFAULT_FULL_FILENAME): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  // Revoke é adiado para não interromper o download em alguns browsers.
  setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_REVOKE_DELAY_MS);
}

export { DEFAULT_FULL_FILENAME, DEFAULT_INDIVIDUAL_FILENAME };
